<template>
  <div>
    <b-alert
      v-if="apiError"
      show
      variant="danger"
      class="p-1"
    >
      {{ apiError }}
    </b-alert>

    <b-tabs
      justified
      lazy
      pills
      class="mb-1"
    >
      <b-tab :title="$t('dispatcher.order-request.review.tabs.basic-info')">
        <b-card class="mb-1">
          <b-row class="align-items-center">
            <b-col
              cols="12"
              md="8"
              class="mb-1 mb-md-0"
            >
              <h4 class="mb-0">
                {{ $t('dispatcher.order-request.review.details.number', { id: request.id || '-' }) }}
              </h4>
              <div
                v-if="hasValue(request.externalPartnerName)"
                class="text-muted mt-1"
              >
                {{ $t('dispatcher.order-request.review.fields.partner') }}: {{ request.externalPartnerName }}
              </div>
            </b-col>
            <b-col
              cols="12"
              md="4"
              class="text-md-right"
            >
              <b-badge
                pill
                :variant="statusVariant(request.status)"
              >
                {{ statusLabel(request.status) }}
              </b-badge>
            </b-col>
          </b-row>
        </b-card>

        <b-alert
          v-if="decisionReason"
          show
          variant="warning"
          class="p-1"
        >
          {{ decisionReason }}
        </b-alert>

        <b-row>
          <b-col
            cols="12"
            md="7"
          >
            <b-card class="mb-1">
              <b-row>
                <b-col
                  cols="12"
                >
                  <strong>{{ $t('partner.order-request.fields.customer') }}</strong>
                  <div v-if="hasValue(resolveCustomerName(request))">
                    {{ resolveCustomerName(request) }}
                  </div>
                </b-col>
                <b-col
                  cols="12"
                  md="6"
                  class="mt-1"
                >
                  <strong>{{ $t('partner.order-request.fields.requested-type') }}</strong>
                  <div>
                    <b-badge
                      pill
                      variant="light-info"
                    >
                      {{ requestedTypeLabel(request.requestedType) }}
                    </b-badge>
                  </div>
                </b-col>
                <b-col
                  cols="12"
                  :md="requestedStartAtColumnMd"
                  class="mt-1"
                >
                  <strong>{{ $t('partner.order-request.fields.requested-start-at') }}</strong>
                  <div>
                    <b-badge
                      variant="light-secondary"
                      class="text-nowrap"
                    >
                      {{ formatDate(request.requestedStartAt) }}
                      <span v-if="showRequestedEndAt">

                        - {{ formatTime(request.requestedEndAt) }}
                      </span>
                    </b-badge>

                  </div>
                </b-col>
                <b-col
                  v-if="hasValue(request.priority)"
                  cols="12"
                  md="6"
                  class="mt-1"
                >
                  <strong>{{ $t('partner.order-request.fields.priority') }}</strong>
                  <div>
                    <b-badge
                      pill
                      :variant="priorityVariant(request.priority)"
                    >
                      {{ priorityLabel(request.priority) }}
                    </b-badge>
                  </div>
                </b-col>
                <b-col
                  v-if="showTransportFlags && (request.returnTransport || request.infectious)"
                  cols="12"
                  class="mt-1"
                >
                  <div
                    v-if="request.returnTransport"
                    class="d-flex align-items-center text-success mb-50"
                  >
                    <span class="mr-50 font-weight-bold">&#10003;</span>
                    <span>{{ $t('order.label.base-order-info.return-transport') }}</span>
                  </div>
                  <div
                    v-if="request.infectious"
                    class="d-flex align-items-center text-warning"
                  >
                    <span class="mr-50 font-weight-bold">&#10003;</span>
                    <span>{{ $t('order.label.base-order-info.infectious') }}</span>
                  </div>
                </b-col>
              </b-row>
            </b-card>

            <b-card
              v-if="showPatientSection"
              class="mb-1"
              :title="$t('order.patient-info.title')"
            >
              <b-row>
                <b-col
                  v-if="hasValue(patientField('surname'))"
                  cols="12"
                  md="6"
                  class="mt-1"
                >
                  <strong>{{ $t('order.patient-info.surname') }}</strong>
                  <div>{{ valueAsText(patientField('surname')) }}</div>
                </b-col>
                <b-col
                  v-if="hasValue(patientField('name'))"
                  cols="12"
                  md="6"
                  class="mt-1"
                >
                  <strong>{{ $t('order.patient-info.name') }}</strong>
                  <div>{{ valueAsText(patientField('name')) }}</div>
                </b-col>
                <b-col
                  v-if="hasValue(patientField('pesel'))"
                  cols="12"
                  md="6"
                  class="mt-1"
                >
                  <strong>{{ $t('order.patient-info.pesel') }}</strong>
                  <div>{{ valueAsText(patientField('pesel')) }}</div>
                </b-col>
                <b-col
                  v-if="hasValue(patientField('sex'))"
                  cols="12"
                  md="6"
                  class="mt-1"
                >
                  <strong>{{ $t('order.patient-info.sex') }}</strong>
                  <div>{{ patientSexLabel(patientField('sex')) }}</div>
                </b-col>
                <b-col
                  v-if="hasValue(patientField('age'))"
                  cols="12"
                  md="6"
                  class="mt-1"
                >
                  <strong>{{ $t('order.patient-info.age') }}</strong>
                  <div>{{ formatAge(patientField('age')) }}</div>
                </b-col>
                <b-col
                  v-if="hasValue(patientField('weight'))"
                  cols="12"
                  md="6"
                  class="mt-1"
                >
                  <strong>{{ $t('order.patient-info.weight') }}</strong>
                  <div>{{ formatWeight(patientField('weight')) }}</div>
                </b-col>
              </b-row>
            </b-card>

            <b-card
              v-if="hasValue(request.notes)"
              class="mb-1"
              :title="$t('partner.order-request.fields.notes')"
            >
              <div>{{ valueAsText(request.notes) }}</div>
            </b-card>
          </b-col>

          <b-col
            cols="12"
            md="5"
          >
            <b-card
              v-if="showPickupSection"
              class="mb-1"
              :title="$t('partner.order-request.fields.pickup')"
            >
              <div
                v-if="hasValue(addressTitle(request.pickup))"
                class="font-weight-bold"
              >
                {{ addressTitle(request.pickup) }}
              </div>
              <div v-if="hasValue(addressLine(request.pickup))">
                {{ addressLine(request.pickup) }}
              </div>
              <div v-if="hasValue(addressZipCity(request.pickup))">
                {{ addressZipCity(request.pickup) }}
              </div>
            </b-card>

            <b-card
              class="mb-1"
              :title="$t(deliveryTitleKey)"
            >
              <div
                v-if="hasValue(addressTitle(request.delivery))"
                class="font-weight-bold"
              >
                {{ addressTitle(request.delivery) }}
              </div>
              <div v-if="hasValue(addressLine(request.delivery))">
                {{ addressLine(request.delivery) }}
              </div>
              <div v-if="hasValue(addressZipCity(request.delivery))">
                {{ addressZipCity(request.delivery) }}
              </div>
            </b-card>
          </b-col>
        </b-row>

        <div
          v-if="canReview || request.acceptedOrderId"
          class="d-flex flex-wrap align-items-center mb-1"
        >
          <b-button
            v-if="canReview"
            variant="success"
            class="mr-1 mb-1"
            :disabled="loading || actionLoading"
            @click="openAcceptModal"
          >
            {{ $t('dispatcher.order-request.review.actions.accept') }}
          </b-button>
          <b-button
            v-if="canReview"
            variant="danger"
            class="mr-1 mb-1"
            :disabled="loading || actionLoading"
            @click="openRejectModal"
          >
            {{ $t('dispatcher.order-request.review.actions.reject') }}
          </b-button>
          <b-button
            v-if="canReview"
            variant="warning"
            class="mr-1 mb-1"
            :disabled="loading || actionLoading"
            @click="openRequestChangesModal"
          >
            {{ $t('dispatcher.order-request.review.actions.request-changes') }}
          </b-button>
          <b-button
            v-if="request.acceptedOrderId"
            variant="outline-primary"
            class="mb-1"
            :to="{ name: 'order-view', params: { id: request.acceptedOrderId } }"
          >
            {{ $t('dispatcher.order-request.review.actions.open-created-order') }}
          </b-button>
        </div>
      </b-tab>

      <b-tab :title="$t('dispatcher.order-request.review.tabs.status-history')">
        <b-card no-body>
          <rc-overlay :show="loading">
            <b-table
              :items="statusHistory"
              :fields="historyFields"
              responsive
              show-empty
              :empty-text="loading ? $t('shared.loading') : $t('shared.no-data')"
              class="rounded-top"
            >
              <template #cell(oldStatus)="data">
                {{ data.item.oldStatus ? statusLabel(data.item.oldStatus) : '-' }}
              </template>
              <template #cell(newStatus)="data">
                {{ statusLabel(data.item.newStatus) }}
              </template>
              <template #cell(changedByUser)="data">
                {{ changedByUserLabel(data.item) }}
              </template>
              <template #cell(createdAt)="data">
                {{ formatDate(data.item.createdAt) }}
              </template>
            </b-table>
          </rc-overlay>
        </b-card>
      </b-tab>
    </b-tabs>

    <div class="d-flex justify-content-end mt-1">
      <b-button
        variant="flat-secondary"
        :to="{ name: 'dispatcher-order-request-review-list' }"
      >
        {{ $t('partner.order-request.actions.back-to-list') }}
      </b-button>
    </div>

    <dispatcher-review-accept-modal
      :visible="showAcceptModal"
      :loading="actionLoading"
      @close="showAcceptModal = false"
      @confirm="submitAccept"
    />

    <dispatcher-review-decision-modal
      :visible="showRejectModal"
      :loading="actionLoading"
      :title="$t('dispatcher.order-request.review.reject.title')"
      :confirm-label="$t('dispatcher.order-request.review.actions.reject')"
      @close="showRejectModal = false"
      @confirm="submitReject"
    />

    <dispatcher-review-decision-modal
      :visible="showRequestChangesModal"
      :loading="actionLoading"
      :title="$t('dispatcher.order-request.review.request-changes.title')"
      :confirm-label="$t('dispatcher.order-request.review.actions.request-changes')"
      @close="showRequestChangesModal = false"
      @confirm="submitRequestChanges"
    />
  </div>
</template>

<script>
import {
  BAlert,
  BBadge,
  BButton,
  BCard,
  BCol,
  BRow,
  BTab,
  BTabs,
  BTable,
} from 'bootstrap-vue'
import RcOverlay from '@/layouts/components/rc/RcOverlay.vue'
import { parseRequestError, transformDatetimeToHumanReadablePretty } from '@/helpers/helpers'
import PusherClient from '@/libs/pusher'
import orderTypes from '@/dictionaries/orderTypes'
import {
  getPriorityLabelKey,
  getPriorityVariant,
  mapApiPriorityToCode,
} from '@/views/external-order-request/helpers/priority'
import { transformTimeToHumanReadablePretty } from '@/helpers/dateTimeHelper'
import DispatcherReviewAcceptModal from './components/DispatcherReviewAcceptModal.vue'
import DispatcherReviewDecisionModal from './components/DispatcherReviewDecisionModal.vue'
import {
  getExternalOrderRequestStatusVariant,
  isReviewableStatus,
} from './helpers/reviewFormModel'

export default {
  name: 'DispatcherOrderRequestReviewDetails',
  components: {
    BAlert,
    BBadge,
    BButton,
    BCard,
    BCol,
    BRow,
    BTab,
    BTabs,
    BTable,
    RcOverlay,
    DispatcherReviewAcceptModal,
    DispatcherReviewDecisionModal,
  },
  data() {
    return {
      loading: false,
      actionLoading: false,
      apiError: null,
      showAcceptModal: false,
      showRejectModal: false,
      showRequestChangesModal: false,
      customerNameFallback: null,
      refreshTimeoutId: null,
      pusherExternalOrderRequestHandler: null,
    }
  },
  computed: {
    request() {
      return this.$store.getters['externalOrderRequest/getDispatcherReviewRequest']
    },
    statusHistory() {
      return this.request.statusHistory || []
    },
    canReview() {
      return isReviewableStatus(this.request.status)
    },
    isEventRequest() {
      return this.request?.requestedType === orderTypes.event
    },
    isMedicalCourierRequest() {
      return this.request?.requestedType === orderTypes.medical_courier
    },
    showRequestedEndAt() {
      return this.isEventRequest && this.hasValue(this.request?.requestedEndAt)
    },
    requestedStartAtColumnMd() {
      if (this.isEventRequest && !this.showRequestedEndAt) {
        return 12
      }

      return 6
    },
    showTransportFlags() {
      return !this.isEventRequest && !this.isMedicalCourierRequest
    },
    showPatientSection() {
      return !this.isEventRequest && !this.isMedicalCourierRequest
    },
    showPickupSection() {
      return !this.isEventRequest
    },
    deliveryTitleKey() {
      return this.isEventRequest
        ? 'partner.order-request.fields.execution-location'
        : 'partner.order-request.fields.delivery'
    },
    decisionReason() {
      if (this.request.rejectionReason) {
        return this.request.rejectionReason
      }

      if (this.request.changesRequestReason) {
        return this.request.changesRequestReason
      }

      return null
    },
    historyFields() {
      return [
        { key: 'oldStatus', label: this.$t('partner.order-request.history.old-status') },
        { key: 'newStatus', label: this.$t('partner.order-request.history.new-status') },
        { key: 'reason', label: this.$t('partner.order-request.history.reason') },
        { key: 'changedByUser', label: this.$t('partner.order-request.history.changed-by-user') },
        { key: 'createdAt', label: this.$t('partner.order-request.history.changed-at') },
      ]
    },
  },
  watch: {
    '$route.params.id': {
      handler() {
        this.initializePage()
      },
    },
  },
  created() {
    this.initializePage()
  },
  mounted() {
    this.subscribeToPusher()
  },
  beforeDestroy() {
    this.unsubscribeFromPusher()
    if (this.refreshTimeoutId) {
      clearTimeout(this.refreshTimeoutId)
      this.refreshTimeoutId = null
    }
  },
  methods: {
    async initializePage() {
      this.loading = true
      this.apiError = null
      this.customerNameFallback = null
      this.$store.commit('externalOrderRequest/CLEAR_DISPATCHER_REVIEW_REQUEST')

      try {
        await this.$store.dispatch('externalOrderRequest/fetchDispatcherReviewRequestById', this.$route.params.id)
        this.resolveCustomerNameFallback()
      } catch (error) {
        parseRequestError(error)
        this.apiError = error?.response?.data?.message || this.$t('dispatcher.order-request.review.errors.fetch-details')
      } finally {
        this.loading = false
      }
    },
    scheduleRealtimeRefresh() {
      if (this.refreshTimeoutId) {
        return
      }

      this.refreshTimeoutId = setTimeout(() => {
        this.refreshTimeoutId = null
        this.initializePage()
      }, 350)
    },
    subscribeToPusher() {
      if (this.pusherExternalOrderRequestHandler) {
        return
      }

      this.pusherExternalOrderRequestHandler = payload => {
        const payloadId = payload?.id
        if (payloadId !== undefined && payloadId !== null
          && String(payloadId) !== String(this.$route.params.id)) {
          return
        }

        this.scheduleRealtimeRefresh()
      }

      PusherClient.bind('update', 'external_order_request', this.pusherExternalOrderRequestHandler, this)
    },
    unsubscribeFromPusher() {
      if (!this.pusherExternalOrderRequestHandler) {
        return
      }

      PusherClient.unbind('update', 'external_order_request', this.pusherExternalOrderRequestHandler, this)
      this.pusherExternalOrderRequestHandler = null
    },
    openAcceptModal() {
      if (!this.canReview || this.actionLoading) {
        return
      }
      this.showAcceptModal = true
    },
    openRejectModal() {
      if (!this.canReview || this.actionLoading) {
        return
      }
      this.showRejectModal = true
    },
    openRequestChangesModal() {
      if (!this.canReview || this.actionLoading) {
        return
      }
      this.showRequestChangesModal = true
    },
    async submitAccept(payload) {
      const decisionPayload = await this.executeDecision('externalOrderRequest/acceptDispatcherReviewRequest', payload)
      if (decisionPayload) {
        this.showAcceptModal = false
        const acceptedOrderId = decisionPayload.acceptedOrderId || this.request?.acceptedOrderId || null
        if (acceptedOrderId) {
          this.$router.push({ name: 'order-edit', params: { id: acceptedOrderId } })
        }
      }
    },
    async submitReject(payload) {
      const decisionPayload = await this.executeDecision('externalOrderRequest/rejectDispatcherReviewRequest', payload)
      if (decisionPayload) {
        this.showRejectModal = false
      }
    },
    async submitRequestChanges(payload) {
      const decisionPayload = await this.executeDecision('externalOrderRequest/requestChangesDispatcherReviewRequest', payload)
      if (decisionPayload) {
        this.showRequestChangesModal = false
      }
    },
    async executeDecision(action, data) {
      this.actionLoading = true
      this.apiError = null

      try {
        const response = await this.$store.dispatch(action, {
          id: this.$route.params.id,
          data,
        })
        await this.$store.dispatch('externalOrderRequest/fetchDispatcherReviewStatusTotals')
        return response?.data || {}
      } catch (error) {
        parseRequestError(error)
        this.apiError = error?.response?.data?.message || this.$t('dispatcher.order-request.review.errors.decision')
        return null
      } finally {
        this.actionLoading = false
      }
    },
    resolveCustomerNameFallback() {
      if (this.request?.customerName) {
        this.customerNameFallback = this.request.customerName
        return
      }

      if (!this.request?.customerId) {
        this.customerNameFallback = null
        return
      }

      this.$store.dispatch('customer/fetchCustomerById', this.request.customerId)
        .then(response => {
          this.customerNameFallback = response?.data?.name || null
        })
        .catch(() => {
          this.customerNameFallback = null
        })
    },
    resolveCustomerName(request) {
      if (request?.customerName) {
        return request.customerName
      }

      if (this.customerNameFallback) {
        return this.customerNameFallback
      }

      return request?.customerId || null
    },
    requestedTypeLabel(type) {
      if (!type) {
        return '-'
      }

      const i18nKey = `order.types.${type}`
      if (typeof this.$te === 'function' && this.$te(i18nKey)) {
        return this.$t(i18nKey)
      }

      return type
    },
    priorityLabel(priorityValue) {
      const priorityCode = mapApiPriorityToCode(priorityValue)
      if (!priorityCode) {
        return '-'
      }

      return this.$t(getPriorityLabelKey(priorityCode))
    },
    priorityVariant(priorityValue) {
      const priorityCode = mapApiPriorityToCode(priorityValue)
      return getPriorityVariant(priorityCode)
    },
    statusLabel(status) {
      if (!status) {
        return '-'
      }

      const i18nKey = `partner.order-request.statuses.${status}`
      if (typeof this.$te === 'function' && this.$te(i18nKey)) {
        return this.$t(i18nKey)
      }

      return status
    },
    statusVariant(status) {
      return getExternalOrderRequestStatusVariant(status)
    },
    formatDate(value) {
      if (!value) {
        return '-'
      }

      return transformDatetimeToHumanReadablePretty(value)
    },
    formatTime(value) {
      if (!value) {
        return ''
      }

      return transformTimeToHumanReadablePretty(value)
    },
    changedByUserLabel(item) {
      const user = item?.changedByUser
      if (!user || typeof user !== 'object') {
        return '-'
      }

      if (this.hasValue(user.fullName)) {
        return user.fullName
      }

      const chunks = []
      if (this.hasValue(user.name)) {
        chunks.push(user.name)
      }
      if (this.hasValue(user.surname)) {
        chunks.push(user.surname)
      }

      return chunks.length > 0 ? chunks.join(' ') : '-'
    },
    hasValue(value) {
      if (value === null || value === undefined) {
        return false
      }
      if (typeof value === 'string') {
        return value.trim() !== ''
      }
      if (Array.isArray(value)) {
        return value.length > 0
      }
      return true
    },
    valueAsText(value) {
      return this.hasValue(value) ? String(value) : null
    },
    formatAge(age) {
      if (!this.hasValue(age)) {
        return null
      }

      const yearsLabel = this.$t('order.patient-info.years')
      const unit = yearsLabel === 'order.patient-info.years' ? 'lat' : yearsLabel
      return `${age} ${unit}`
    },
    formatWeight(weight) {
      if (!this.hasValue(weight)) {
        return null
      }

      return `${weight} kg`
    },
    patientSexLabel(sex) {
      if (!this.hasValue(sex)) {
        return null
      }

      const normalized = String(sex).toLowerCase()
      if (normalized === 'm') {
        return this.$t('order.patient-info.M')
      }
      if (normalized === 'f') {
        return this.$t('order.patient-info.F')
      }

      return String(sex)
    },
    patientField(field) {
      if (!this.request || typeof this.request !== 'object') {
        return null
      }

      const { patient } = this.request
      if (!patient || typeof patient !== 'object') {
        return null
      }

      return Object.prototype.hasOwnProperty.call(patient, field) ? patient[field] : null
    },
    addressTitle(payload) {
      if (!payload || typeof payload !== 'object') {
        return null
      }

      return this.valueAsText(payload.name || payload.label || payload.alias)
    },
    addressLine(payload) {
      if (!payload || typeof payload !== 'object') {
        return null
      }

      return this.valueAsText(payload.address || payload.addressLine1 || payload.street)
    },
    addressZipCity(payload) {
      if (!payload || typeof payload !== 'object') {
        return null
      }

      const zip = payload.zip || payload.postalCode || payload.postcode || ''
      const city = payload.city || payload.town || ''
      const line = `${zip} ${city}`.trim()

      return line === '' ? null : line
    },
  },
}
</script>

<style scoped lang="scss">
.gap-1 {
  gap: 0.5rem;
}
</style>
